// src/pages/PatientReminders.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import ReminderCard from "./ReminderCard";
import ReminderEditor from "./ReminderEditor";
import QueryAnswersOverlay from "./queries/QueryAnswersOverlay";
import { CUSTOM_QUERY_TEMPLATE, buildQueryConfig, queryFormState } from "./queries/QueryFields";


const API = process.env.REACT_APP_API_URL;

const DOW = [
  { key: "MO", label: "Mo" },
  { key: "TU", label: "Di" },
  { key: "WE", label: "Mi" },
  { key: "TH", label: "Do" },
  { key: "FR", label: "Fr" },
  { key: "SA", label: "Sa" },
  { key: "SU", label: "So" },
];

const WEEKS_OF_MONTH = [
  { key: 1, label: "1." },
  { key: 2, label: "2." },
  { key: 3, label: "3." },
  { key: 4, label: "4." },
  { key: -1, label: "Letzte" },
];

function toLocalDatetimeInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("sv-SE").replace(" ", "T").slice(0, 16);
}

function getKind(r) {
  const raw = (r.recur_kind || r.kind || "").toString().toLowerCase();
  if (raw === "one_off" || raw === "oneoff") return "one_off";
  if (raw === "daily") return "daily";
  if (raw === "weekly") return "weekly";
  if (raw === "monthly") return "monthly";
  return "one_off";
}

function fromLocalDatetimeInput(localStr) {
  if (!localStr) return null;
  const d = new Date(localStr);
  return d.toISOString();
}

function earliestLabelRefreshAt(reminders) {
  return reminders.reduce((earliest, reminder) => {
    if (!reminder.next_occurrence_label_valid_until) return earliest;
    const timestamp = new Date(reminder.next_occurrence_label_valid_until).getTime();
    if (Number.isNaN(timestamp)) return earliest;
    return earliest === null || timestamp < earliest ? timestamp : earliest;
  }, null);
}

export default function RemindersLogic({ patientId, multi = false, templateType = null }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [overlayOpen, setOverlayOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [answersFor, setAnswersFor] = useState(null);
  const loadRequestRef = React.useRef(0);

  // FILTER (default = All Active)
  // keys: active_all | active_global | active_local | active_events | inactive_all
  const [filterKey, setFilterKey] = useState("active_all");

  // If we’re on the global/multi page, restrict options to Active/Inactive only.
  useEffect(() => {
    if (multi && !["active_all", "inactive_all"].includes(filterKey)) {
      setFilterKey("active_all");
    }
  }, [multi, filterKey]);

  const listUrl = React.useCallback(() => {
    //if (multi) return `${API}/reminders?entry_type=GLOBAL_REMINDER`;
    if (multi) return `${API}/reminder-series?entry_type=GLOBAL_REMINDER`;
    if (patientId) return `${API}/reminders?patient_id=${patientId}`;
    return `${API}/reminders`;
  }, [multi, patientId]);

  const createUrl       = `${API}/reminders`;
  const updateUrl       = (id)       => `${API}/reminders/${id}`;
  const deleteUrl       = (id)       => `${API}/reminders/${id}`;
  //const deleteSeriesUrl = (seriesId) => `${API}/reminders/series/${seriesId}`;
  //const deleteSeriesUrl = (seriesId) => `${API}/reminder-series/${seriesId}`;
  const deleteSeriesUrl = (seriesId) => `${API}/reminders/series/${seriesId}`;

  const loadItems = useCallback(async ({ showLoading = false } = {}) => {
    const requestId = ++loadRequestRef.current;
    if (showLoading) setLoading(true);
    try {
      setErr("");
      const res = await fetch(listUrl(), { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      if (requestId === loadRequestRef.current) {
        setItems(Array.isArray(data) ? data : data?.reminders || []);
      }
    } catch (e) {
      if (requestId === loadRequestRef.current) {
        setErr(e?.message || "Fehler beim Laden der Erinnerungen");
      }
    } finally {
      if (showLoading && requestId === loadRequestRef.current) setLoading(false);
    }
  }, [listUrl]);

  // helper flags
  const isEvent = (r) => ((r.entry_type || r.type || "") + "").toUpperCase() === "EVENT";
  const isGlobalReminder = (r) => r.entry_type === "GLOBAL_REMINDER";
  const isLocalReminder = useCallback(
    (r) => !isGlobalReminder(r) && !isEvent(r),
    []
  );
  // FILTERED list
  const pageItems = useMemo(() => {
    if (!templateType) return items;
    return items.filter((item) =>
      templateType === "activation"
        ? item.template_type === "activation_routine"
        : templateType === "query"
        ? item.is_query
        : item.template_type !== "activation_routine" && !item.is_query
    );
  }, [items, templateType]);

  const filteredItems = useMemo(() => {
    switch (filterKey) {
      case "active_global":
        return pageItems.filter((r) => r.is_active && isGlobalReminder(r));
      case "active_local":
        return pageItems.filter((r) => r.is_active && isLocalReminder(r));
      case "active_events":
        return pageItems.filter((r) => r.is_active && isEvent(r));
      case "inactive_all":
        return pageItems.filter((r) => !r.is_active);
      case "active_all":
      default:
        return pageItems.filter((r) => r.is_active);
    }
  }, [pageItems, filterKey, isLocalReminder]);

  const initialForm = React.useMemo(
    () => ({
      title: "",
      description: "",
      is_active: true,
      sleep_mode_after_reminder: true,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      kind: "one_off",
      at_local: "",
      mode: "times",
      times: [],
      every_minutes: "60",
      window_start: "08:00",
      window_end: "18:00",
      week_days: { MO: false, TU: false, WE: false, TH: false, FR: false, SA: false, SU: false },
      month_weeks: { "1": false, "2": false, "3": false, "4": false, "-1": false },
      month_days: { MO: false, TU: false, WE: false, TH: false, FR: false, SA: false, SU: false },
      recipients: multi
        ? { mode: "all", ids: [] }
        : { mode: "custom", ids: patientId ? [patientId] : [] },
    }),
    [multi, patientId]
  );
  const [form, setForm] = useState(initialForm);

  function resetForm() {
    setForm(initialForm);
    setEditing(null);
  }

  // Initial load. Later refreshes keep the existing list visible.
  useEffect(() => {
    void loadItems({ showLoading: true });
    return () => { loadRequestRef.current += 1; };
  }, [loadItems]);

  const nextLabelRefreshAt = useMemo(() => earliestLabelRefreshAt(items), [items]);

  // The backend label expires at the next occurrence or local midnight.
  // Refresh the whole list once, instead of running one timer per card.
  useEffect(() => {
    if (nextLabelRefreshAt === null) return undefined;
    const delay = Math.max(nextLabelRefreshAt - Date.now() + 1000, 0);
    const id = setTimeout(() => { void loadItems(); }, delay);
    return () => clearTimeout(id);
  }, [loadItems, nextLabelRefreshAt]);

  // Background tabs may throttle timers, so refresh again when the tab returns.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void loadItems();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [loadItems]);

  function openCreate() {
    resetForm();
    setOverlayOpen(true);
  }

  async function openEdit(r) {
    const tplId = r.prompt_template_id ?? "custom";
    const lockedByTpl = tplId !== "custom" && tplId !== CUSTOM_QUERY_TEMPLATE;

    const base = {
      ...initialForm,
      title: r.title || "",
      description: r.description || "",
      is_active: !!r.is_active,
      sleep_mode_after_reminder: r.sleep_mode_after_reminder !== false,
      timezone: r.timezone || initialForm.timezone,

      template_id: tplId,
      locked_title: lockedByTpl,
      locked_description: lockedByTpl,
      query: r.query_config ? queryFormState(r.query_config) : undefined,
    };

    if (multi && r.series_id) {
      try {
        //const res = await fetch(`${API}/reminders?series_id=${encodeURIComponent(r.series_id)}`, {
        //  credentials: "include",
        //});
        const res = await fetch(`${API}/reminder-series/${encodeURIComponent(r.series_id)}/rows`, 
          { credentials: "include" 
        });
        if (res.ok) {
          const rows = await res.json();
          const ids = Array.from(new Set(rows.map(x => x.patient_id).filter(Boolean)));
          base.recipients = { mode: "custom", ids };
        } else {
          base.recipients = { mode: "custom", ids: r.patient_id ? [r.patient_id] : [] };
        }
      } catch {
        base.recipients = { mode: "custom", ids: r.patient_id ? [r.patient_id] : [] };
      }
    } else if (multi) {
      base.recipients = { mode: "custom", ids: r.patient_id ? [r.patient_id] : [] };
    }

    const rules = r.rules || {};
    const kind = getKind(r);

    if (kind === "one_off") {
      const iso = r.starts_at;
      setForm({ ...base, kind: "one_off", at_local: toLocalDatetimeInput(iso) });
    } else if (kind === "daily") {
      const next = { ...base, kind: "daily", mode: "times", times: [] };
      if (Array.isArray(rules.times) && rules.times.length) {
        next.mode = "times"; next.times = [...rules.times];
      } else if (rules.frequency) {
        next.mode = "frequency";
        next.every_minutes = String(rules.frequency.every_minutes || 60);
        next.window_start = rules.frequency.window_start || next.window_start;
        next.window_end = rules.frequency.window_end || next.window_end;
      }
      setForm(next);
    } else if (kind === "weekly") {
      const next = { ...base, kind: "weekly", mode: "times", times: [], week_days: { ...initialForm.week_days } };
      (rules.days_of_week || []).forEach(d => { if (d in next.week_days) next.week_days[d] = true; });
      if (Array.isArray(rules.times) && rules.times.length) {
        next.mode = "times"; next.times = [...rules.times];
      } else if (rules.frequency) {
        next.mode = "frequency";
        next.every_minutes = String(rules.frequency.every_minutes || 60);
        next.window_start = rules.frequency.window_start || next.window_start;
        next.window_end = rules.frequency.window_end || next.window_end;
      }
      setForm(next);
    } else if (kind === "monthly") {
      const next = { ...base, kind: "monthly", mode: "times", times: [], month_weeks: { ...initialForm.month_weeks }, month_days: { ...initialForm.month_days } };
      (rules.weeks_of_month || []).forEach(w => { next.month_weeks[String(w)] = true; });
      (rules.days_of_week || []).forEach(d => { if (d in next.month_days) next.month_days[d] = true; });
      if (Array.isArray(rules.times) && rules.times.length) {
        next.mode = "times"; next.times = [...rules.times];
      } else if (rules.frequency) {
        next.mode = "frequency";
        next.every_minutes = String(rules.frequency.every_minutes || 60);
        next.window_start = rules.frequency.window_start || next.window_start;
        next.window_end = rules.frequency.window_end || next.window_end;
      }
      setForm(next);
    } else {
      setForm({ ...base, kind: "one_off" });
    }

    setEditing(r);
    setOverlayOpen(true);
  }

  function buildRulesFromForm() {
    const k = form.kind;

    if (k === "one_off") {
      return {};
    }

    if (k === "daily") {
      if (form.mode === "times") {
        return { type: "daily", times: form.times || [] };
      }
      return {
        type: "daily",
        frequency: {
          every_minutes: Number(form.every_minutes || 0) || 60,
          window_start: form.window_start || null,
          window_end: form.window_end || null,
        },
      };
    }

    if (k === "weekly") {
      const days = DOW.filter((d) => form.week_days[d.key]).map((d) => d.key);
      if (form.mode === "times") {
        return { type: "weekly", days_of_week: days, times: form.times || [] };
      }
      return {
        type: "weekly",
        days_of_week: days,
        frequency: {
          every_minutes: Number(form.every_minutes || 0) || 60,
          window_start: form.window_start || null,
          window_end: form.window_end || null,
        },
      };
    }

    if (k === "monthly") {
      const weeks = WEEKS_OF_MONTH.filter((w) => form.month_weeks[String(w.key)]).map((w) => w.key);
      const days = DOW.filter((d) => form.month_days[d.key]).map((d) => d.key);
      if (form.mode === "times") {
        return { type: "monthly", weeks_of_month: weeks, days_of_week: days, times: form.times || [] };
      }
      return {
        type: "monthly",
        weeks_of_month: weeks,
        days_of_week: days,
        frequency: {
          every_minutes: Number(form.every_minutes || 0) || 60,
          window_start: form.window_start || null,
          window_end: form.window_end || null,
        },
      };
    }

    return null;
  }

  async function submit() {
    setSaving(true);
    setErr("");
    try {
      const audience =
        multi
          ? { mode: form.recipients.mode, ids: form.recipients.ids || [] }
          : { mode: "custom", ids: [patientId] };

      const payload = {
        title: form.title.trim(),
        description: form.description || null,
        is_active: !!form.is_active,
        sleep_mode_after_reminder: form.sleep_mode_after_reminder !== false,
        timezone: form.timezone || "UTC",
        kind: form.kind,
        rules: buildRulesFromForm(),
        audience,
        is_global: !!multi,
        prompt_template_id: form.template_id && form.template_id !== "custom" ? form.template_id : null,
      };

      if (form.kind === "one_off") {
        payload.starts_at = fromLocalDatetimeInput(form.at_local);
      }

      if (templateType === "query" && form.query) {
        payload.query_config = buildQueryConfig(form.query, form.template_id === CUSTOM_QUERY_TEMPLATE);
      }

      if (editing && multi && (form.recipients.ids || []).length) {
        payload.audience = { mode: "custom", ids: form.recipients.ids };
      }

      const url = editing ? updateUrl(editing.id) : createUrl;
      const method = editing ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      const saved = await res.json();

      const savedList = Array.isArray(saved) ? saved : [saved];

      if (editing) {
        if (multi) {
          const rep = savedList.reduce((min, r) => (r.id < min.id ? r : min), savedList[0]);
          setItems(arr => [rep, ...arr.filter(x => x.series_id !== rep.series_id)]);
        } else {
          setItems(arr => arr.map(r => (r.id === editing.id ? savedList[0] : r)));
        }
      } else if (multi) {
        const rep = savedList.reduce((min, r) => (r.id < min.id ? r : min), savedList[0]);
        setItems(arr => [rep, ...arr.filter(x => x.series_id !== rep.series_id)]);
      } else {
        setItems(arr => [savedList[0], ...arr]);
      }

      setOverlayOpen(false);
      resetForm();
    } catch (e) {
      setErr(e?.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(rem) {
    try {
      if (!multi) {
        const res = await fetch(deleteUrl(rem.id), { method: "DELETE", credentials: "include" });
        if (!res.ok && res.status !== 404) throw new Error(await res.text());
        setItems(arr => arr.filter(r => r.id !== rem.id));
        return;
      }

      if (!rem.series_id) throw new Error("series_id fehlt – kann Serie nicht löschen.");
      const res = await fetch(deleteSeriesUrl(rem.series_id), { method: "DELETE", credentials: "include" });
      if (!res.ok && res.status !== 404) throw new Error(await res.text());
      setItems(arr => arr.filter(r => r.series_id !== rem.series_id));
    } catch (e) {
      alert(e?.message || "Löschen fehlgeschlagen.");
    }
  }

  const itemName = templateType === "activation" ? "Aktivierung" : templateType === "query" ? "Abfrage" : "Erinnerung";
  const itemNamePlural = templateType === "activation" ? "Aktivierungen" : templateType === "query" ? "Abfragen" : "Erinnerungen";
  const filterOptions = multi || templateType === "activation" || templateType === "query"
    ? [
        { value: "active_all", label: `Alle aktiven ${itemNamePlural}` },
        { value: "inactive_all", label: `Alle inaktiven ${itemNamePlural}` },
      ]
    : [
        { value: "active_all", label: "Alle aktiven Erinnerungen" },
        { value: "active_global", label: "Alle aktiven allgemeinen Erinnerungen" },
        { value: "active_local", label: "Alle aktiven Benutzer Erinnerungen" },
        { value: "active_events", label: "Alle aktiven Kalender-Erinnerungen" },
        { value: "inactive_all", label: "Alle inaktiven Erinnerungen" },
      ];

  return (
    <section className="mt-6 rounded-xl border border-black/10 bg-[#d8edff]/60 p-4 md:p-6">
      {/* Top bar */}
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-3 rounded-2xl bg-[#76B3FF] px-4 py-3 text-white text-lg font-semibold shadow-md hover:bg-[#5d9ef7] active:scale-[.99]"
        >
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/20">+</span>
          Neue {itemName}
        </button>

        {/* Filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Filter</span>
          <div className="relative">
            <select
              aria-label="Filter"
              className="appearance-none rounded-xl border border-blue-200 bg-white/80 px-4 py-2 pr-8 text-sm text-gray-700 shadow-sm"
              value={filterKey}
              onChange={(e) => setFilterKey(e.target.value)}
            >
              {filterOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">▾</span>
          </div>
        </div>
      </div>

      {/* Loading / error */}
      {loading && <div className="text-sm text-gray-700">Lade {itemNamePlural}…</div>}
      {err && !loading && (
        <div className="mb-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
          {err}
        </div>
      )}

      {/* Empty state */}
      {!loading && filteredItems.length === 0 && (
        <div className="text-sm text-gray-700">Keine {itemNamePlural} passend zum Filter.</div>
      )}

      {/* Grid of cards */}
      {!loading && filteredItems.length > 0 && (
        <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredItems.map((r) => {
            const isGlobal = isGlobalReminder(r);
            const canEdit = r.canEdit !== false && (multi || !isGlobal);
            const canDelete = r.canEdit !== false && !isEvent(r);
            return (
              <ReminderCard
                key={multi ? r.series_id : r.id}
                reminder={r}
                isActivation={templateType === "activation"}
                isGlobal={isGlobal}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={canEdit ? (rem) => openEdit(rem) : undefined}
                onDelete={canDelete ? (rem) => remove(rem) : undefined}
                onShowAnswers={r.is_query ? (rem) => setAnswersFor(rem) : undefined}
                showLatestAnswer={!multi}
              />
            );
          })}
        </ul>
      )}

      {answersFor && (
        <QueryAnswersOverlay
          reminder={answersFor}
          patientId={multi ? null : patientId}
          onClose={() => setAnswersFor(null)}
        />
      )}

      {/* Overlay */}
      <ReminderEditor
        patientId = {patientId}
        open={overlayOpen}
        saving={saving}
        form={form}
        setForm={setForm}
        onSubmit={submit}
        onClose={() => setOverlayOpen(false)}
        DOW={DOW}
        WEEKS_OF_MONTH={WEEKS_OF_MONTH}
        multi={multi}
        isEditing={!!editing}
        templateType={templateType}
      />
    </section>
  );
}
